import { useMemo, useRef } from "react"
import { useAppContext, useDataInjector } from "@ciq/shared/hooks/context"
import Api from "@ciq/shared/api/api"
import { ErrorToast } from "@ciq/shared/utils/toast"

const SettingsSourceDataKey = {
  KEY: "settings_source",
  DEFAULT_DATA: {
    isLoaded: false,
    data: [],
  },
}

export function useDataSourceSource() {
  const { addLoadingId, removeLoadingId } = useAppContext()
  const { data: sourceData, setData, reload } = useDataInjector(SettingsSourceDataKey)

  const addLoadingIdRef = useRef(addLoadingId)
  const removeLoadingIdRef = useRef(removeLoadingId)
  const setDataRef = useRef(setData)
  const resetRef = useRef(reload)

  const actions = useMemo(
    () => ({
      reset: resetRef.current,

      getSources: () => {
        const handleLoading = loading => {
          if (loading) {
            addLoadingIdRef.current("source_settings_load")
            setDataRef.current({ isLoaded: false })
          } else {
            removeLoadingIdRef.current("source_settings_load")
            setDataRef.current({ isLoaded: false })
          }
        }

        handleLoading(true)
        const controller = new AbortController()
        Api.get(`settings/source`, { signal: controller.signal })
          .then(response => {
            setDataRef.current({ data: response.data.sources, isLoaded: false })
            removeLoadingIdRef.current("source_settings_load")
          })
          .catch(e => {
            handleLoading(false)

            if (e.handled) return

            console.error(e.error)
            ErrorToast("Unable to fetch Source")
          })
      },
      loadSources: () => {
        const loadingId = "globalDatasource#create"
        addLoadingIdRef.current(loadingId)
        const controller = new AbortController()
        Api.post(`settings/source/load`, {}, { signal: controller.signal })
          .then(response => setDataRef.current({ data: response.data.sources, isLoaded: false }))
          .catch(e => {
            if (e.handled) return
            console.error(e.error)
            ErrorToast("Unable to add Source")
          })
          .finally(() => removeLoadingIdRef.current(loadingId))
      },
    }),
    []
  )

  return useMemo(
    () => ({
      isLoaded: sourceData.isLoaded,

      sources: sourceData.data,
      hasSources: sourceData.data.length > 0,

      actions,
    }),
    [actions, sourceData]
  )
}
